/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { IFilterDisplayAngularComp } from 'ag-grid-angular';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import type { FilterDisplayParams, ValueGetterParams, IRowNode } from 'ag-grid-community';
import { BLANK_TOKEN, normalize } from '@shared/utils/set-filter.utils';

// for now expect only primitive values in row
type FilterValue = string | number | null;

type CustomFilterDisplayParams<TData = any> = FilterDisplayParams<TData, any, any, any> & {
  preselectedValues?: FilterValue[];
};

type FilterModel = {
  values: Set<string>;
};

@Component({
  selector: 'app-custom-set-filter',
  imports: [MatCheckboxModule, MatButtonModule],
  template: `
    <div class="custom-filter-container">
      <div class="filter-actions">
        <button mat-button (click)="selectAll()">Select All</button>
        <button mat-button (click)="deselectAll()">Deselect All</button>
      </div>

      <div class="filter-options">
        <!-- For big amount of data consider virtual scroll & search -->
        @for (value of allValues(); track value) {
          <div class="filter-option">
            <mat-checkbox [checked]="isSelected(value)" (change)="onCheckboxChange(value, $event.checked)">
              {{ displayValue(value) }}
            </mat-checkbox>
          </div>
        }
      </div>
    </div>
  `,
  styles: `
    .custom-filter-container {
      padding: 8px;
      max-height: 300px;
      overflow-y: auto;
    }

    .filter-actions {
      display: flex;
      gap: 8px;
      margin-bottom: 8px;
      border-bottom: 1px solid #ddd;
      padding-bottom: 8px;
    }

    .filter-actions button {
      font-size: 12px;
      min-width: auto;
      padding: 4px 8px;
    }

    .filter-options {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .filter-option {
      display: flex;
      align-items: center;
    }

    mat-checkbox {
      font-size: 14px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomSetFilterComponent<TData = any> implements IFilterDisplayAngularComp<TData, any, Set<string>> {
  allValues = signal<FilterValue[]>([]);
  filterParams!: CustomFilterDisplayParams<TData>;
  readonly blanksToken = BLANK_TOKEN;
  readonly normalize = normalize;

  private selected = new Set<string>();
  private isEvaluated = false;

  private rebuildValues(params: CustomFilterDisplayParams<TData>): void {
    // get value from node
    const accessor = (node: IRowNode<TData>): FilterValue => {
      if (params.colDef.valueGetter) {
        const valueGetter = params.colDef.valueGetter as (p: ValueGetterParams<TData>) => FilterValue;
        return valueGetter({
          data: node.data,
          node: node,
          colDef: params.colDef,
          getValue: (field: string) => (node.data as any)?.[field],
          api: params.api,
          column: params.column,
          context: params.context,
        });
      } else if (params.colDef.field) {
        return node.data ? (node.data as any)[params.colDef.field] : null;
      }
      return null;
    };

    const uniq = new Set<string>();
    params.api.forEachLeafNode(node => {
      const value = accessor(node);
      const key = this.normalize(value);
      uniq.add(key);
    });

    const toOriginal = (key: string): FilterValue => (key === this.blanksToken ? null : JSON.parse(key));

    this.allValues.set(
      Array.from(uniq)
        .map(toOriginal)
        .sort((a, b) => {
          if (a === null) return -1;
          if (b === null) return 1;
          if (typeof a === 'number' && typeof b === 'number') return a - b;
          return String(a).localeCompare(String(b));
        })
    );

    if (!this.isEvaluated) {
      const preselected = params.preselectedValues;
      if (preselected && preselected.length > 0) {
        this.selected = new Set(preselected.map(v => this.normalize(v)));
      } else {
        this.selected = new Set(this.allValues().map(v => this.normalize(v)));
      }
      this.isEvaluated = true;
    } else {
      // Clean up obsolete values
      this.selected.forEach(key => {
        if (!uniq.has(key)) {
          this.selected.delete(key);
        }
      });
    }
  }

  agInit(params: CustomFilterDisplayParams<TData>): void {
    this.filterParams = params;
    this.refresh(params);
    this.onModelChange();
  }

  refresh(newParams: CustomFilterDisplayParams<TData>): boolean {
    this.rebuildValues(newParams);
    return true;
  }

  onNewRowsLoaded?(): void {
    this.rebuildValues(this.filterParams);
    this.onModelChange();
  }

  // Checkbox functionality
  isSelected(value: FilterValue): boolean {
    return this.selected.has(this.normalize(value));
  }

  onCheckboxChange(value: FilterValue, checked: boolean): void {
    const key = this.normalize(value);
    if (checked) {
      this.selected.add(key);
    } else {
      this.selected.delete(key);
    }
    this.onModelChange();
  }

  selectAll(): void {
    this.selected = new Set(this.allValues().map(v => this.normalize(v)));
    this.onModelChange();
  }

  deselectAll(): void {
    this.selected.clear();
    this.onModelChange();
  }

  displayValue(value: FilterValue): string {
    if (value === null || value === undefined) {
      return '(Blanks)';
    }
    return String(value);
  }

  private onModelChange(): void {
    if (this.filterParams?.onModelChange) {
      const model = this.getModel();
      this.filterParams.onModelChange(model);
    }
  }

  getModel(): FilterModel | null {
    if (!this.isFilterActive()) {
      return null;
    }
    return {
      values: this.selected,
    };
  }

  isFilterActive(): boolean {
    return this.allValues().length > 0 && this.selected.size !== this.allValues().length;
  }
}
